import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Select,
  Input,
  Tag,
  Button,
  Modal,
  Space,
  Tooltip,
  Avatar,
} from "antd";
import {
  CommentOutlined,
  UserOutlined,
  CalendarOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { all_routes } from "../router/all_routes";
import TicketListModal from "../../core/modals/ticketListModal";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import api from "../../services/api";
import { showBasicAlert } from "../../core/common/useAlert";
import moment from "moment";
import CommonSelect from "../../core/common/commonSelect";
import { useSelector } from "react-redux";
import { getEmployeeImageUrl } from "../../services/assetCommonService";
import Datatable from "../../core/common/dataTable";
import TicketFilters from "./TicketFilters";
import TicketMetrics from "./TicketMetrics";
import DeleteModal from "../../core/modals/deleteModal";

const { Option } = Select;
const { TextArea } = Input;

// Define types
interface Comment {
  id: number;
  comment: string;
  createdAt: string;
  user_id: number;
  t_ticket_id: number;
}

interface TicketRecord {
  id: number;
  title: string;
  description: string;
  priority: number;
  operation_status: number;
  category_id: number;
  raised_by: number;
  assigned_to: number | null;
  raised_date: string;
  updatedAt: string;
  createdAt: string;
  product_id?: number;
  due_date?: string | null;
}

const Tickets = () => {
  const user = useSelector((state: any) => state.user?.user);
  // Add null check for user
  const role_char = user?.userDetails?.role_char?.toLowerCase() || "employee";
  const routes = all_routes;
  const signature = localStorage.getItem("signature") || "";

  const truncateText = (text: string, limit: number) => {
    if (!text) return "";
    return text.length > limit ? text.slice(0, limit) + "..." : text;
  };

  // State variables
  const [categories, setCategories] = useState<
    { key: number; Category: string }[]
  >([]);
  const [allRaisedTicket, setAllRaisedTicket] = useState<TicketRecord[]>([]);
  const [ticketStatusCount, setTicketStatusCount] = useState<any>({});
  const [employeeDetail, setEmployeeDetail] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<number | null>(null);
  const [selectedOperationStatus, setSelectedOperationStatus] = useState<
    number | null
  >(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRaisedBy, setSelectedRaisedBy] = useState<number | null>(null);
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<number | null>(
    null
  );
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Comments modal state
  const [isCommentsModalVisible, setIsCommentsModalVisible] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState<number | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>("");
  const [commentCounts, setCommentCounts] = useState<Record<number, number>>(
    {}
  );
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Cache for employee data
  const [employeeNameCache, setEmployeeNameCache] = useState<Record<number, string>>({});
  const [employeeImageCache, setEmployeeImageCache] = useState<Record<number, string>>({});

  const [viewModal, setViewModal] = useState({
    open: false,
    title: "",
    content: "",
  });

  // Company state
  const [formDataCompanyChoose, setformDataCompanyChoose] = useState({
    signature: signature,
    companyName: "Select Company",
    companyId: "",
    companyType: "",
  });

  // Single delete modal state for both ticket and comment
  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    id: number | null;
    type: 'ticket' | 'comment' | null;
    title: string;
    message: string;
  }>({
    visible: false,
    id: null,
    type: null,
    title: '',
    message: ''
  });

  const [assignModal, setAssignModal] = useState<{
    open: boolean;
    ticketId: number | null;
    assignedTo: number | null;
    dueDate: string;
    categoryId: number | null;
  }>({
    open: false,
    ticketId: null,
    assignedTo: null,
    dueDate: "",
    categoryId: null,
  });

  // Fetch UI data
  const fetchUI = async () => {
    try {
      const [employeeDetails, getAllCompanyList] = await Promise.all([
        api.post("userService/getEmployeesListData", { signature }),
        api.post("userService/getAllCompanyList", { signature }),
      ]);

      setEmployeeDetail(employeeDetails.data?.data?.data?.allUsers || []);
      setCompanies(getAllCompanyList?.data?.data?.data || []);
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error fetching data"
      );
      console.error("Error fetching dashboard data:", err);
    }
  };

  // Build employee caches
  useEffect(() => {
    if (employeeDetail.length > 0) {
      const nameCache: Record<number, string> = {};
      const imageCache: Record<number, string> = {};

      employeeDetail.forEach((emp: any) => {
        const empId = parseInt(emp.key);
        nameCache[empId] = emp.Name;
        imageCache[empId] = getEmployeeImageUrl(empId, employeeDetail, user);
      });

      setEmployeeNameCache(nameCache);
      setEmployeeImageCache(imageCache);
    }
  }, [employeeDetail, user]);

  // Bulk comment count fetch
  const fetchBulkCommentCounts = async (ticketIds: number[]) => {
    try {
      const response = await api.post("raiseTicketsService/getBulkCommentCounts", {
        signature,
        ticket_ids: ticketIds
      });

      const counts = response.data?.message?.[0]?.data || {};
      setCommentCounts(counts);
    } catch (error) {
      console.error("Error fetching comment counts:", error);
    }
  };

  // Update UI with tickets
  const updateUI = async () => {
    try {
      if (
        !formDataCompanyChoose?.companyId ||
        !formDataCompanyChoose?.companyType
      )
        return;

      setLoading(true);
      const [allTicketCategories, allTickets, allTicketsCounts] =
        await Promise.all([
          api.post("raiseTicketsService/getAllTicketCategories", {
            signature,
            company_id: formDataCompanyChoose.companyId,
            company_type: formDataCompanyChoose.companyType,
          }),
          api.post("raiseTicketsService/getAllTickets", {
            signature,
            company_id: formDataCompanyChoose.companyId,
            company_type: formDataCompanyChoose.companyType,
          }),
          api.post("raiseTicketsService/getTicketCounts", {
            signature,
            company_id: formDataCompanyChoose.companyId,
            company_type: formDataCompanyChoose.companyType,
          }),
        ]);

      const categoriesData = allTicketCategories.data?.data?.data || [];
      setCategories(categoriesData);

      const tickets = allTickets.data?.message[0]?.data || [];
      setAllRaisedTicket(tickets);
      setTicketStatusCount(allTicketsCounts.data?.message[0]?.data || {});

      // Fetch comment counts in bulk after tickets load
      if (tickets.length > 0) {
        const ticketIds = tickets.map((t: TicketRecord) => t.id);
        await fetchBulkCommentCounts(ticketIds);
      }
    } catch (err: any) {
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error fetching data"
      );
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Company options
  const companyChoose = companies.map((company: any) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));

  // Handle company change
  const handleCompanyChange = async (selectedOption: any) => {
    if (!selectedOption) return;
    const newFormData = {
      companyId: selectedOption.value,
      companyName:
        selectedOption.label.split("-")[0]?.trim() || "All Companies",
      companyType: selectedOption.label.split("-")[1]?.trim() || "Ticket",
    };

    setformDataCompanyChoose((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  useEffect(() => {
    fetchUI();
  }, []);

  useEffect(() => {
    if (
      formDataCompanyChoose?.companyId &&
      formDataCompanyChoose?.companyType
    ) {
      updateUI();
    }
  }, [formDataCompanyChoose]);

  useEffect(() => {
    if (companies.length > 0) {
      handleCompanyChange({
        value: companies[0].id.toString(),
        label: companies[0].name + " - " + companies[0].type,
      });
    }
  }, [companies]);

  // Priority mapping
  const getPriority = (priority: number) => {
    switch (priority) {
      case 0:
        return { text: "Low", color: "green" };
      case 1:
        return { text: "Medium", color: "orange" };
      case 2:
        return { text: "High", color: "red" };
      default:
        return { text: "Unknown", color: "default" };
    }
  };

  // Operation status mapping
  const getOperationStatus = (status: number) => {
    switch (status) {
      case 1:
        return { text: "New", color: "pink" };
      case 2:
        return { text: "Open", color: "purple" };
      case 3:
        return { text: "Pending", color: "orange" };
      case 4:
        return { text: "Closed", color: "green" };
      default:
        return { text: "Unknown", color: "default" };
    }
  };

  // Get category name
  const getCategoryName = (category_id: number) => {
    const category = categories.find((cat) => cat.key === category_id);
    return category ? category.Category : "Unknown Category";
  };

  // Use cache for employee name
  const getEmployeeName = (employeeId: number) => {
    return employeeNameCache[employeeId] || "Unknown";
  };

  const handleAssignSelect = (
    record: TicketRecord,
    assignedTo: number | null
  ) => {
    if (!assignedTo) {
      handleAssignSave(record, null, null);
      return;
    }

    setAssignModal({
      open: true,
      ticketId: record.id,
      assignedTo,
      dueDate: "",
      categoryId: record.category_id,
    });
  };

  const handleAssignSave = async (
    record: TicketRecord,
    assignedTo: number | null,
    dueDate: string | null
  ) => {
    try {
      await api.post("raiseTicketsService/assignTicket", {
        signature,
        id: record.id,
        assigned_to: assignedTo,
        due_date: dueDate,
        operation_status: assignedTo ? 2 : 1,
      });

      setAllRaisedTicket((prev) =>
        prev.map((t) =>
          t.id === record.id
            ? {
              ...t,
              assigned_to: assignedTo,
              due_date: dueDate,
            }
            : t
        )
      );

      showBasicAlert("Ticket updated successfully");
    } catch (error) {
      console.error(error);
      showBasicAlert("Failed to update ticket");
    } finally {
      setAssignModal({
        open: false,
        ticketId: null,
        assignedTo: null,
        dueDate: "",
        categoryId: null,
      });
    }
  };

  const employeeOptions = [
    { value: null, label: "Unassigned", imageUrl: null },
    ...employeeDetail
      .filter((emp: any) => {
        return (
          String(emp.CompanyId) === formDataCompanyChoose.companyId &&
          emp.CompanyType === formDataCompanyChoose.companyType &&
          emp.status === 1
        );
      })
      .map((emp: any) => ({
        value: Number(emp.key),
        label: emp.Name,
        imageUrl: employeeImageCache[Number(emp.key)] || null,
        companyId: emp.CompanyId,
        companyType: emp.CompanyType,
        disable: false,
      })),
  ];

  // Memoized filtered tickets
  const filteredTickets = useMemo(() => {
    return allRaisedTicket.filter((ticket: TicketRecord) => {

      const raisedByExists = ticket.raised_by
        ? employeeOptions.some(opt => opt.value === ticket.raised_by)
        : true;

      if (!raisedByExists) {
        return false;
      }
      const matchesCategory =
        selectedCategory === null || ticket.category_id === selectedCategory;
      const matchesPriority =
        selectedPriority === null || ticket.priority === selectedPriority;
      const matchesOperationStatus =
        selectedOperationStatus === null ||
        ticket.operation_status === selectedOperationStatus;
      const matchesSearch =
        searchTerm === "" ||
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(ticket.id).padStart(3, "0").includes(searchTerm);
      const matchesRaisedBy =
        selectedRaisedBy === null || ticket.raised_by === selectedRaisedBy;
      const matchesAssignedTo =
        selectedAssignedTo === null || ticket.assigned_to === selectedAssignedTo;

      return (
        matchesCategory &&
        matchesPriority &&
        matchesOperationStatus &&
        matchesSearch &&
        matchesRaisedBy &&
        matchesAssignedTo
      );
    });
  }, [allRaisedTicket, selectedCategory, selectedPriority,
    selectedOperationStatus, searchTerm, selectedRaisedBy, selectedAssignedTo]);

  // Handle edit
  const handleEdit = async (id: number) => {
    try {
      const response = await api.post(
        "raiseTicketsService/getRaiseTicketById",
        {
          signature,
          id,
        }
      );

      const editData = response.data?.message?.[0]?.data;
      setSelectedTicket(editData);
    } catch (error) {
      console.error("Error fetching ticket for edit:", error);
      showBasicAlert("Failed to fetch ticket details.");
    }
  };

  const handleDelete = (id: number) => {
    setDeleteModal({
      visible: true,
      id: id,
      type: 'ticket',
      title: 'Delete Ticket',
      message: 'Are you sure you want to delete this ticket? This action cannot be undone.'
    });
  };

  // Handle priority update
  const handlePriorityUpdate = async (
    ticketId: number,
    newPriority: number
  ) => {
    try {
      const response = await api.post("raiseTicketsService/updatePriority", {
        signature,
        id: ticketId,
        priority: newPriority,
      });

      if (response.status === 200 || response.status === 201) {
        setAllRaisedTicket((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket.id === ticketId
              ? { ...ticket, priority: newPriority }
              : ticket
          )
        );
        showBasicAlert("Priority updated successfully");
      }
    } catch (error) {
      console.error("Error updating ticket priority:", error);
      showBasicAlert("Failed to update ticket priority.");
    }
  };

  // Handle operation status update
  const handleOperationStatusUpdate = async (
    ticketId: number,
    newStatus: number
  ) => {
    try {
      const currentTicket = allRaisedTicket.find(
        (ticket) => ticket.id === ticketId
      );
      if (!currentTicket) {
        showBasicAlert("Ticket not found.");
        return;
      }

      const response = await api.post(
        "raiseTicketsService/updateOperationTicketStatus",
        {
          signature,
          id: ticketId,
          operation_status: newStatus,
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Update local state
        setAllRaisedTicket((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket.id === ticketId
              ? { ...ticket, operation_status: newStatus }
              : ticket
          )
        );

        // Update counts
        setTicketStatusCount((prev: any) => ({
          ...prev,
          [`operationStatus_${newStatus}`]: (prev[`operationStatus_${newStatus}`] || 0) + 1,
          [`operationStatus_${currentTicket.operation_status}`]:
            Math.max((prev[`operationStatus_${currentTicket.operation_status}`] || 0) - 1, 0)
        }));

        showBasicAlert("Status updated successfully");
      }
    } catch (error: any) {
      console.error("Error updating ticket status:", error);
      showBasicAlert(error?.data?.message[0]?.msg);
    }
  };

  // Comments Modal Functions
  const showCommentsModal = async (ticketId: number) => {
    setCurrentTicketId(ticketId);
    setIsCommentsModalVisible(true);
    await fetchComments(ticketId);
  };

  const fetchComments = async (ticketId: number) => {
    try {
      setCommentsLoading(true);
      const response = await api.post(
        "raiseTicketsService/getCommentsByTicketId",
        {
          signature,
          t_ticket_id: ticketId,
        }
      );

      const allComments = response.data?.message?.[0]?.data || [];
      setComments(
        allComments.map((comment: any) => ({
          id: comment.id,
          comment: comment.comment,
          createdAt: comment.createdAt,
          user_id: comment.user_id,
          t_ticket_id: comment.t_ticket_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentTicketId) return;

    try {
      const tempId = Date.now();
      const newCommentObj = {
        id: tempId,
        comment: newComment,
        createdAt: new Date().toISOString(),
        user_id: parseInt(localStorage.getItem("user_id") || "0"),
        t_ticket_id: currentTicketId,
      };

      setComments((prev) => [...prev, newCommentObj]);
      setCommentCounts((prev) => ({
        ...prev,
        [currentTicketId]: (prev[currentTicketId] || 0) + 1,
      }));
      setNewComment("");

      const res = await api.post(
        "raiseTicketsService/addCommentAndUpdateStatus",
        {
          t_ticket_id: currentTicketId,
          comment: newComment,
          signature,
        }
      );

      const serverComment = res.data?.message?.[0]?.data;
      if (serverComment && serverComment.id) {
        setComments((prev) =>
          prev.map((comment) =>
            comment.id === tempId
              ? { ...comment, id: serverComment.id }
              : comment
          )
        );
      }

      await fetchComments(currentTicketId);
    } catch (error) {
      console.error("Error adding comment:", error);
      if (currentTicketId) await fetchComments(currentTicketId);
    }
  };

  const handleUpdateComment = async (
    commentId: number,
    updatedText: string
  ) => {
    if (!currentTicketId) return;

    try {
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? { ...comment, comment: updatedText }
            : comment
        )
      );
      setEditingCommentId(null);
      setEditCommentText("");

      await api.post("raiseTicketsService/updateComment", {
        signature,
        id: commentId,
        t_ticket_id: currentTicketId,
        comment: updatedText,
      });

      await fetchComments(currentTicketId);
    } catch (error) {
      console.error("Error updating comment:", error);
      if (currentTicketId) await fetchComments(currentTicketId);
    }
  };

  const handleDeleteComment = (commentId: number) => {
    setDeleteModal({
      visible: true,
      id: commentId,
      type: 'comment',
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment? This action cannot be undone.'
    });
  };


  const confirmDelete = async () => {
    if (!deleteModal.id || !deleteModal.type) {
      setDeleteModal({ visible: false, id: null, type: null, title: '', message: '' });
      return;
    }

    try {
      if (deleteModal.type === 'ticket') {
        // Delete ticket logic
        const response = await api.post("raiseTicketsService/deleteRaiseTicket", {
          signature,
          id: deleteModal.id,
        });

        const deletedTicket = allRaisedTicket.find(t => t.id === deleteModal.id);
        setAllRaisedTicket(prev => prev.filter(ticket => ticket.id !== deleteModal.id));

        if (deletedTicket) {
          setTicketStatusCount((prev: any) => ({
            ...prev,
            [`operationStatus_${deletedTicket.operation_status}`]:
              Math.max((prev[`operationStatus_${deletedTicket.operation_status}`] || 0) - 1, 0)
          }));
        }

        showBasicAlert(response.data?.message[0]?.msg || "Ticket deleted successfully.");
      }
      else if (deleteModal.type === 'comment') {
        // Delete comment logic
        if (!currentTicketId) return;

        setComments((prev) => prev.filter((comment) => comment.id !== deleteModal.id));
        setCommentCounts((prev) => ({
          ...prev,
          [currentTicketId]: Math.max((prev[currentTicketId] || 0) - 1, 0),
        }));

        await api.post("raiseTicketsService/deleteComment", {
          signature,
          id: deleteModal.id,
        });

        await fetchComments(currentTicketId);
        showBasicAlert("Comment deleted successfully");
      }
    } catch (error: any) {
      console.error(`Error deleting ${deleteModal.type}:`, error);
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        `Failed to delete ${deleteModal.type}. Please try again.`;
      showBasicAlert(errorMessage);

      // Refresh data on error
      if (deleteModal.type === 'comment' && currentTicketId) {
        await fetchComments(currentTicketId);
      }
    } finally {
      setDeleteModal({ visible: false, id: null, type: null, title: '', message: '' });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedCategory(null);
    setSelectedPriority(null);
    setSelectedOperationStatus(null);
    setSelectedRaisedBy(null);
    setSelectedAssignedTo(null);
  };

  // Add this near getEmployeeName
  const getEmployeeStatus = (employeeId: number): "active" | "inactive" | "suspended" | "unknown" => {
    const emp = employeeDetail.find((e: any) => parseInt(e.key) === employeeId);
    if (!emp) return "unknown";
    if (emp.status === 1) return "active";
    if (emp.status === 3) return "suspended";
    if (emp.status === 0) return "inactive";
    return "unknown";
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "suspended": return "User suspended";
      case "inactive": return "User inactive";
      default: return "User inactive";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "suspended": return "#faad14";
      case "inactive": return "#ff4d4f";
      default: return "#ff4d4f";
    }
  };

  // Show clear button condition
  const showClearButton = useMemo(() => {
    return !!(searchTerm ||
      selectedCategory !== null ||
      selectedPriority !== null ||
      selectedOperationStatus !== null ||
      selectedRaisedBy !== null ||
      selectedAssignedTo !== null);
  }, [searchTerm, selectedCategory, selectedPriority,
    selectedOperationStatus, selectedRaisedBy, selectedAssignedTo]);

  // Ant Design Table Columns
  const columns: ColumnsType<TicketRecord> = [
    {
      title: "Ticket ID",
      dataIndex: "id",
      key: "id",
      render: (id: number) => (
        <span className="fw-semibold text-primary">
          #{String(id).padStart(3, "0")}
        </span>
      ),
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Ticket Info",
      dataIndex: "title",
      key: "title",
      width: "40%",
      render: (text: string, record: TicketRecord) => {
        const raisedByName = getEmployeeName(record.raised_by);
        const imageUrl = employeeImageCache[record.raised_by];

        // const raisedByExists = record.raised_by
        //   ? employeeOptions.some(opt => opt.value === record.raised_by)
        //   : true;

        const raisedByStatus = record.raised_by ? getEmployeeStatus(record.raised_by) : "active";
        const raisedByExists = raisedByStatus === "active";

        return (
          <div className="d-flex">
            <div style={{ minWidth: "110px" }}>
              <div
                style={{ position: "relative", cursor: "pointer", width: "fit-content" }}
              >
                {imageUrl ? (
                  <Avatar size={42} src={imageUrl} />
                ) : (
                  <Avatar size={42} icon={<UserOutlined />} />
                )}
                <span
                  style={{
                    position: "absolute",
                    top: "-28px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#000",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "0.3s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* {raisedByExists ? raisedByName : "User inactive"} */}
                  {raisedByExists ? raisedByName : getStatusLabel(raisedByStatus)}

                </span>
              </div>
              <small
                className="text-muted d-flex align-items-center mt-1"
                style={{ gap: "4px", fontSize: "11px" }}
              >
                <CalendarOutlined />{" "}
                {moment(record.createdAt).format("MMM DD")}
              </small>
            </div>
            <div style={{ marginLeft: "4px" }}>
              <h6 className="mb-1 fw-semibold" style={{ color: "#333" }}>
                {truncateText(record.title, 20)}
                {record.title.length > 20 && (
                  <span
                    style={{
                      color: "#1677FF",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                    onClick={() =>
                      setViewModal({
                        open: true,
                        title: "Ticket Title",
                        content: record.title,
                      })
                    }
                  >
                    {" "}
                    View More
                  </span>
                )}
              </h6>
              <p
                className="text-muted mb-0"
                style={{ fontSize: "0.83rem", marginTop: "-2px" }}
              >
                {truncateText(record.description, 35)}
                {record.description.length > 35 && (
                  <span
                    style={{
                      color: "#1677FF",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                    onClick={() =>
                      setViewModal({
                        open: true,
                        title: "Description",
                        content: record.description,
                      })
                    }
                  >
                    {" "}
                    View More
                  </span>
                )}
              </p>
              {/* Optionally show inactive badge next to the name */}
              {/* {!raisedByExists && (
                <small style={{ color: '#ff4d4f', fontSize: '11px' }}>
                  (Inactive user)
                </small>
              )} */}
              {!raisedByExists && (
                <small style={{ color: getStatusColor(raisedByStatus), fontSize: '11px' }}>
                  ({raisedByStatus === "suspended" ? "Suspended user" : "Inactive user"})
                </small>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category_id",
      key: "category_id",
      width: "12%",
      render: (category_id: number) => (
        <Tag color="blue">{getCategoryName(category_id)}</Tag>
      ),
      filters: categories.map((cat) => ({
        text: cat.Category,
        value: cat.key,
      })),
      onFilter: (value, record) => record.category_id === value,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: "10%",
      render: (priority: number, record: TicketRecord) => {
        const priorityData = getPriority(priority);
        return role_char !== "employee" ? (
          <Select
            value={priority}
            onChange={(value) => handlePriorityUpdate(record.id, value)}
            style={{ width: "100%" }}
            size="small"
          >
            <Option value={0}>
              <Tag color="green">Low</Tag>
            </Option>
            <Option value={1}>
              <Tag color="orange">Medium</Tag>
            </Option>
            <Option value={2}>
              <Tag color="red">High</Tag>
            </Option>
          </Select>
        ) : (
          <Tag color={priorityData.color}>{priorityData.text}</Tag>
        );
      },
      filters: [
        { text: "Low", value: 0 },
        { text: "Medium", value: 1 },
        { text: "High", value: 2 },
      ],
      onFilter: (value, record) => record.priority === value,
    },
    {
      title: "Status",
      dataIndex: "operation_status",
      key: "operation_status",
      width: "12%",
      render: (status: number, record: TicketRecord) => {
        const statusData = getOperationStatus(status);
        return role_char !== "employee" ? (
          <Select
            value={status}
            onChange={(value) => handleOperationStatusUpdate(record.id, value)}
            style={{ width: "100%" }}
            size="small"
          >
            <Option value={1}>
              <Tag color="pink">New</Tag>
            </Option>
            <Option value={2}>
              <Tag color="purple">Open</Tag>
            </Option>
            <Option value={3}>
              <Tag color="orange">Pending</Tag>
            </Option>
            <Option value={4}>
              <Tag color="green">Closed</Tag>
            </Option>
          </Select>
        ) : (
          <Tag color={statusData.color}>{statusData.text}</Tag>
        );
      },
      filters: [
        { text: "New", value: 1 },
        { text: "Open", value: 2 },
        { text: "Pending", value: 3 },
        { text: "Closed", value: 4 },
      ],
      onFilter: (value, record) => record.operation_status === value,
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_to",
      key: "assigned_to",
      width: "14%",
      render: (assigned_to: number | null, record: TicketRecord) => {
        if (role_char === "employee") {
          if (!assigned_to)
            return <span className="text-muted">Unassigned</span>;
          return (
            <Space size="small">
              <Avatar
                size="small"
                src={assigned_to ? employeeImageCache[assigned_to] : undefined}
                icon={<UserOutlined />}
              />
              <span className="small">{assigned_to ? getEmployeeName(assigned_to) : "Unknown"}</span>
            </Space>
          );
        }

        // const assignedInOptions = assigned_to ? employeeOptions.some(opt => opt.value === assigned_to) : true;

        // let selectValue: any = assigned_to ?? null;
        // if (assigned_to && !assignedInOptions) {
        //   selectValue = {
        //     value: assigned_to,
        //     label: "User inactive"
        //   };
        // }
        const assignedStatus = assigned_to ? getEmployeeStatus(assigned_to) : "active";
        const assignedInOptions = assignedStatus === "active";

        let selectValue: any = assigned_to ?? null;
        if (assigned_to && !assignedInOptions) {
          selectValue = {
            value: assigned_to,
            label: getStatusLabel(assignedStatus)   // "User inactive" or "User suspended"
          };
        }
        return (
          <Select
            // value={assigned_to ?? null}
            value={selectValue}
            style={{ width: 200 }}
            size="small"
            allowClear
            showSearch
            optionFilterProp="label"
            onChange={(value) => handleAssignSelect(record, value)}
            options={employeeOptions}
            optionRender={(option) => (
              <Space>
                {option.data.imageUrl && (
                  <Avatar
                    size="small"
                    src={option.data.imageUrl}
                    icon={<UserOutlined />}
                  />
                )}
                <span>{option.data.label}</span>
              </Space>
            )}
          />
        );
      },
    },
    {
      title: "Comments",
      key: "comments",
      render: (_, record: TicketRecord) => (
        <Button
          type="default"
          size="small"
          icon={<CommentOutlined />}
          onClick={() => showCommentsModal(record.id)}
        >
          {commentCounts[record.id] || 0}
        </Button>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "9%",
      render: (_, record: TicketRecord) => (
        <Space>
          {role_char !== "employee" && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.id)}
              title="Edit"
              data-bs-toggle="modal"
              data-bs-target="#edit_ticket"
            />
          )}
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            title="Delete"
            data-bs-toggle="modal"
            data-bs-target="#delete_modal"
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Tickets</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Tickets
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="dropdown me-2 mb-2">
                <CommonSelect
                  className="select"
                  options={companyChoose}
                  defaultValue={{
                    value: `${formDataCompanyChoose.companyId}-${formDataCompanyChoose.companyType}`,
                    label:
                      formDataCompanyChoose.companyName +
                      "-" +
                      formDataCompanyChoose.companyType,
                  }}
                  onChange={handleCompanyChange}
                />
              </div>
              <div className="mb-2">
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_ticket"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Ticket
                </Link>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Ticket Metrics */}
          <TicketMetrics counts={ticketStatusCount} />

          {/* Tickets Table */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h5 className="card-title mb-0">Ticket List</h5>
                <TicketFilters
                  categories={categories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedPriority={selectedPriority}
                  setSelectedPriority={setSelectedPriority}
                  selectedOperationStatus={selectedOperationStatus}
                  setSelectedOperationStatus={setSelectedOperationStatus}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  onClearFilters={handleClearFilters}
                  showClearButton={showClearButton}
                />
              </div>
            </div>
            <div className="card-body p-0">
              <Datatable columns={columns} dataSource={filteredTickets} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Comments Modal */}
      <Modal
        title={
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "#e6f7ff",
                }}
              >
                <CommentOutlined
                  style={{ fontSize: "20px", color: "#1890ff" }}
                />
              </div>
              <div>
                <h5 className="mb-0">Discussion</h5>
                <small className="text-muted">
                  {commentCounts[currentTicketId || 0] || 0}{" "}
                  {commentCounts[currentTicketId || 0] === 1
                    ? "comment"
                    : "comments"}
                </small>
              </div>
            </div>
          </div>
        }
        open={isCommentsModalVisible}
        onCancel={() => {
          setIsCommentsModalVisible(false);
          setCurrentTicketId(null);
          setNewComment("");
          setEditingCommentId(null);
          setEditCommentText("");
        }}
        footer={null}
        width={750}
        style={{ top: 20 }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "70vh" }}>
          {/* Add Comment Form */}
          <div
            className="p-3 mb-3"
            style={{
              backgroundColor: "#fafafa",
              borderRadius: "12px",
              border: "1px solid #f0f0f0",
            }}
          >
            <form onSubmit={handleAddComment}>
              <div className="d-flex align-items-start gap-3">
                <div style={{ flexShrink: 0 }}>
                  {user?.userDetails?.user_id ? (
                    (() => {
                      const currentUserImageUrl = employeeImageCache[parseInt(user.userDetails.user_id)];
                      return currentUserImageUrl ? (
                        <Avatar size={40} src={currentUserImageUrl} />
                      ) : (
                        <Avatar
                          size={40}
                          style={{
                            backgroundColor: "#1890ff",
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          {user?.userDetails?.name?.charAt(0)?.toUpperCase() ||
                            "U"}
                        </Avatar>
                      );
                    })()
                  ) : (
                    <Avatar
                      size={40}
                      icon={<UserOutlined />}
                      style={{ backgroundColor: "#1890ff" }}
                    />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <TextArea
                    rows={3}
                    placeholder="Share your thoughts..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{
                      borderRadius: "8px",
                      resize: "none",
                      fontSize: "14px",
                    }}
                    className="mb-2"
                  />
                  <div className="d-flex justify-content-end">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!newComment.trim()}
                      style={{
                        borderRadius: "6px",
                        height: "36px",
                        paddingLeft: "20px",
                        paddingRight: "20px",
                      }}
                    >
                      Post Comment
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Comments List */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "8px",
            }}
          >
            {commentsLoading ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "300px" }}
              >
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center text-muted"
                style={{ height: "300px" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <CommentOutlined
                    style={{ fontSize: "32px", color: "#bfbfbf" }}
                  />
                </div>
                <h6 className="mb-2">No comments yet</h6>
                <p className="text-center" style={{ fontSize: "14px" }}>
                  Be the first to share your thoughts!
                </p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {[...comments]
                  .filter((c) => c.t_ticket_id === currentTicketId)
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((comment, index) => {
                    const employee = employeeDetail.find(
                      (emp: any) => parseInt(emp.key) === comment.user_id
                    );
                    const employeeName = employee
                      ? employee.Name
                      : "Managing Director";
                    const imageUrl = employeeImageCache[comment.user_id];
                    const isEditing = editingCommentId === comment.id;
                    const isCurrentUser =
                      parseInt(user?.userDetails?.id) === comment.user_id;


                    return (
                      <div
                        key={comment.id}
                        style={{
                          padding: "16px",
                          backgroundColor: index === 0 ? "#f6ffed" : "#fff",
                          borderRadius: "12px",
                          border: `1px solid ${index === 0 ? "#b7eb8f" : "#f0f0f0"}`,
                        }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <div style={{ flexShrink: 0 }}>
                            {imageUrl ? (
                              <Avatar size={42} src={imageUrl} />
                            ) : (
                              <Avatar
                                size={42}
                                style={{
                                  backgroundColor: isCurrentUser
                                    ? "#1890ff"
                                    : "#52c41a",
                                  fontSize: "16px",
                                  fontWeight: "600",
                                }}
                              >
                                {employeeName.charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <h6
                                    className="mb-0"
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: "600",
                                      color: "#262626",
                                    }}
                                  >
                                    {employeeName}
                                  </h6>
                                  {isCurrentUser && (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        padding: "2px 8px",
                                        backgroundColor: "#e6f7ff",
                                        color: "#1890ff",
                                        borderRadius: "4px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      You
                                    </span>
                                  )}
                                  {index === 0 && (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        padding: "2px 8px",
                                        backgroundColor: "#f6ffed",
                                        color: "#52c41a",
                                        borderRadius: "4px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Latest
                                    </span>
                                  )}
                                </div>
                                <small
                                  className="text-muted"
                                  style={{ fontSize: "12px" }}
                                >
                                  <CalendarOutlined
                                    style={{
                                      marginRight: "4px",
                                      fontSize: "12px",
                                    }}
                                  />
                                  {moment(comment.createdAt).fromNow()}
                                </small>
                              </div>
                              {isCurrentUser && (
                                <div className="d-flex align-items-center gap-1">
                                  <Tooltip title="Edit comment">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={
                                        <EditOutlined
                                          style={{ fontSize: "14px" }}
                                        />
                                      }
                                      onClick={() => {
                                        setEditingCommentId(comment.id);
                                        setEditCommentText(comment.comment);
                                      }}
                                      style={{
                                        color: "#8c8c8c",
                                        height: "28px",
                                        width: "28px",
                                      }}
                                    />
                                  </Tooltip>
                                  <Button
                                    type="text"
                                    size="small"
                                    data-bs-toggle="modal"
                                    data-bs-target="#delete_modal"
                                    danger
                                    icon={<DeleteOutlined style={{ fontSize: "14px" }} />}
                                    onClick={() => handleDeleteComment(comment.id)}
                                    style={{ height: "28px", width: "28px" }}
                                    title="Delete comment"
                                  />
                                </div>
                              )}
                            </div>
                            {isEditing ? (
                              <div>
                                <TextArea
                                  rows={3}
                                  value={editCommentText}
                                  onChange={(e) =>
                                    setEditCommentText(e.target.value)
                                  }
                                  style={{
                                    borderRadius: "8px",
                                    resize: "none",
                                    fontSize: "14px",
                                  }}
                                  className="mb-2"
                                  autoFocus
                                />
                                <Space>
                                  <Button
                                    type="primary"
                                    size="small"
                                    onClick={() =>
                                      handleUpdateComment(
                                        comment.id,
                                        editCommentText
                                      )
                                    }
                                    disabled={!editCommentText.trim()}
                                    style={{
                                      borderRadius: "6px",
                                      height: "32px",
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setEditingCommentId(null);
                                      setEditCommentText("");
                                    }}
                                    style={{
                                      borderRadius: "6px",
                                      height: "32px",
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Space>
                              </div>
                            ) : (
                              <p
                                className="mb-0"
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "1.6",
                                  color: "#595959",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {comment.comment}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={viewModal.open}
        onCancel={() => setViewModal({ open: false, title: "", content: "" })}
        footer={null}
      >
        <h5 className="fw-bold mb-2">{viewModal.title}</h5>
        <p style={{ whiteSpace: "pre-wrap", lineHeight: "1.5" }}>
          {viewModal.content}
        </p>
      </Modal>

      <Modal
        open={assignModal.open}
        title="Assign Ticket"
        onCancel={() =>
          setAssignModal({
            open: false,
            ticketId: null,
            assignedTo: null,
            dueDate: "",
            categoryId: null,
          })
        }
        onOk={() => {
          if (!assignModal.dueDate) {
            showBasicAlert("Please select a due date");
            return;
          }

          const record = allRaisedTicket.find(
            (t) => t.id === assignModal.ticketId
          );

          if (!record) {
            showBasicAlert("Ticket not found");
            return;
          }

          handleAssignSave(
            record,
            assignModal.assignedTo!,
            assignModal.dueDate
          );
        }}
      >
        <label className="form-label">
          Due Date <span className="text-danger">*</span>
        </label>
        <Input
          type="date"
          min={new Date().toISOString().split("T")[0]}
          value={assignModal.dueDate}
          onChange={(e) =>
            setAssignModal((prev) => ({
              ...prev,
              dueDate: e.target.value,
            }))
          }
        />
      </Modal>

      <DeleteModal
        modalId="delete_modal"
        onDelete={confirmDelete}
        title={deleteModal.title}
        message={deleteModal.message}
      />

      <TicketListModal
        categories={categories}
        selectedTicket={selectedTicket}
        fetchUI={updateUI}
        companyChoose={companyChoose}
        employeeDetail={employeeDetail}
        user={user}
        signature={signature}
      />
    </>
  );
};

export default Tickets;